/**
 * How many are demanding a reset, as one number two very different things draw.
 *
 * `CodexPeeker` puts this many heads over the Codex card and `render/protest.ts` stands this many
 * zombies with placards in OpenAI's courtyard. It lives here, away from both, because it is neither
 * a component nor a renderer: it is the rule, and the two would otherwise each own a copy of it —
 * a card showing five and a picket showing four is the kind of disagreement nobody reports and
 * everybody notices. `src/render` may not import React, which is the other reason this is not in
 * the component that used to hold it.
 */

/** MUST HAVE RESET clicks before the first protester can appear. */
export const PEEKER_MIN_CLICKS = 500

/** A day must pass after a reset before the protest can start again. */
export const PEEKER_MIN_AGE_MS = 24 * 60 * 60 * 1000

/** MUST HAVE RESET clicks per extra zombie. */
export const PEEKER_STEP = 10_000

/** The whole cast. */
export const PEEKER_MAX = 9

/**
 * How many zombies have earned a place in the reset protest.
 *
 * The first arrives only after both the 500th click and a full day since the last reset. The
 * later arrivals retain the ten-thousand-click rhythm, so the counter's existing milestones do
 * not move when the first picket finally appears.
 */
export function peekerCount(
  clicks: number,
  lastResetAt: string | null | undefined,
  now = Date.now(),
): number {
  if (!Number.isFinite(clicks) || clicks < PEEKER_MIN_CLICKS) return 0
  const resetAt = typeof lastResetAt === 'string' ? Date.parse(lastResetAt) : Number.NaN
  if (!Number.isFinite(resetAt) || !Number.isFinite(now) || now - resetAt < PEEKER_MIN_AGE_MS) return 0
  return Math.min(PEEKER_MAX, 1 + Math.floor(clicks / PEEKER_STEP))
}
